import * as React from "react";
import TruncateMarkup, { TruncateProps } from "react-truncate-markup";
import styled, { css } from "styled-components";
import { CSSProperties } from "react";

import {
  FontColors,
  FontSizes,
  FontSpacing,
  FontWeights
} from "src/library/utils";

export interface ITextProps {
  className?: string;
  children?: React.ReactNode;
  fontSize?: keyof FontSizes;
  weight?: keyof FontWeights;
  color?: keyof FontColors | string;
  letterSpacing?: keyof FontSpacing;
  style?: React.CSSProperties;
  textTransform?: CSSProperties["textTransform"];
  display?: React.CSSProperties;
}

export const TextStyles = css<ITextProps>`
  ${({ textTransform }) =>
    !textTransform &&
    css`
      ::first-letter {
        text-transform: uppercase;
      }

      * {
        ::first-letter {
          text-transform: uppercase;
        }
      }
    `}
  ${({
    theme, fontSize, color, weight, letterSpacing, textTransform
  }) => css`
    p& {
      margin-bottom: ${theme.spacing.margins.md};
    }

    h1&,
    h2&,
    h3&,
    h4&,
    h5&,
    h6& {
      font-family: ${theme.fonts.family.heading || "Roboto"}, Helvetica;
    }

    h1& {
      font-weight: ${theme.fonts.weights.bold};
      margin-bottom: ${theme.spacing.margins.md};
      text-transform: capitalize;
      font-size: ${theme.fonts.sizes.h1};
    }

    h2& {
      font-weight: ${theme.fonts.weights.bold};
      margin-bottom: ${theme.spacing.margins.md};
      font-size: ${theme.fonts.sizes.h2};
    }

    h3& {
      font-weight: ${theme.fonts.weights.bold};
      margin-bottom: ${theme.spacing.margins.md};
      font-size: ${theme.fonts.sizes.h3};
    }

    h4& {
      font-weight: ${theme.fonts.weights.bold};
      margin-bottom: ${theme.spacing.margins.md};
      font-size: ${theme.fonts.sizes.h4};
    }

    font-family: ${fontSize && fontSize !== "p" && fontSize !== "sub" ? theme.fonts.family.heading || "Roboto" : "inherit"}, Helvetica;
    text-transform: ${textTransform};
    letter-spacing: ${theme.fonts.spacing[ letterSpacing || "normal" ]};
    font-size: ${theme.fonts.sizes[ fontSize || "p" ]};
    color: ${theme.fonts.colors[ color || "primary" ]};
    font-weight: ${theme.fonts.weights[ weight || "normal" ]};

    strong {
      font-weight: ${theme.fonts.weights.bold};
    }
  `}
  line-height: 1.4em;
`;

export const Text = styled.span<ITextProps>`
  ${TextStyles}
`;

interface ITextTruncateProps extends ITextProps {
  truncateProps?: TruncateProps;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
}

export const TextTruncate: React.ReactFCWithChildren<ITextTruncateProps> = props => {
  return (
    <TruncateMarkup {...props.truncateProps}>
      <Text
        className={props.className}
        as={props.as}
        {...props}
      >
        {props.children}
      </Text>
    </TruncateMarkup>
  );
};
